"use client";

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearch } from '@/context/SearchContext';
import { useCheckout } from '@/context/CheckoutContext';
import { useSession } from '@/context/SessionContext';
import { useAddressManager } from '@/context/AddressManagerContext';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search as SearchIcon, X, ShoppingCart, Plus, Store as StoreIcon, Loader2, MapPin, LocateFixed, Home, Settings, RotateCcw, QrCode } from '@/components/icons';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import useHeaderOffset from '@/hooks/useHeaderOffset';
import { cn, formatCurrency, translateCondition } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { usePullToClose } from '@/hooks/usePullToClose';
import { useScrollLock } from '@/hooks/useScrollLock';
import { searchProductsPaginated } from '@/lib/supabase';
import { motion, AnimatePresence } from 'framer-motion';
import { QuantitySelector } from '@/components/QuantitySelector';
import { useToast } from '@/components/ui/use-toast';
import { addToTempCart, getTempCartAsItems, removeFromTempCart, updateTempCartItemQuantity } from '@/lib/tempCart';
import { addCartItem, getCartForUser, getOrCreateCartForStore, removeCartItem, updateCartItem } from '@/lib/supabase/carts';
import { Heart } from '@/components/icons';
import { useFavorites } from '@/hooks/useFavorites';
import { usePlayFavoriteSound } from '@/hooks/usePlayFavoriteSound';
import { usePlayBeep } from '@/hooks/usePlayBeep';
import { useVibrate } from '@/hooks/useVibrate';
import { useContentSidebar } from '@/context/ContentSidebarContext';
import { getProductById } from '@/lib/supabase/products';
import { getUserAddresses, type UserAddress } from '@/lib/supabase/userAddresses';
import { getStoreAddresses } from '@/lib/supabase/storeAddresses';
import { findCityLandmarkByDisplayName, findNearestCityLandmarkByCoords } from '@/lib/defaultLocations';
import { useNavigate } from 'react-router-dom';
import BarcodeScannerModal from '@/components/BarcodeScannerModal';
import { ScanBarcode } from 'lucide-react';

interface SearchProduct {
  id: string;
  title: string;
  description: string | null;
  fixed_sale_price: number | null;
  created_at?: string;
  cover_url?: string;
  main_image_url?: string | null;
  image_urls?: string[];
  store_id: string;
  store_name: string;
  store_slug?: string | null;
  store_latitude?: number | null;
  store_longitude?: number | null;
  condition?: string | null;
  is_auction?: boolean;
  username?: string;
  stock?: number | null;
}

type Mode = 'landmark' | 'home' | 'gps';
type DistanceFilter = 'any' | '5' | '10' | '25' | '50' | '100';
type ConditionFilter = 'any' | 'new' | 'like_new' | 'used_good' | 'used_fair' | 'for_parts';
type AuctionFilter = 'any' | 'auction' | 'fixed';
type DateFilter = 'any' | '24h' | '7d' | '30d' | '365d';

interface LandmarkSnapshot {
  label: string;
  latitude: number;
  longitude: number;
}

interface GroupedProducts {
  [storeId: string]: {
    store_name: string;
    store_slug?: string | null;
    store_latitude?: number | null;
    store_longitude?: number | null;
    distanceLabel?: string | null;
    distanceKm?: number | null;
    products: SearchProduct[];
  };
}

interface ParsedSearchQuery {
  exactPhrases: string[];
  terms: string[];
  broadTerm: string;
}

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatDistance(km: number): string {
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(1)} km`;
}

function stripHtml(html: string): string {
  if (!html) return '';
  try {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  } catch (err) {
    console.warn('Error limpiando HTML:', err);
    return html;
  }
}

function deriveLandmarkSnapshot(
  relevantLocation: { displayName: string; latitude: number; longitude: number } | null | undefined
): LandmarkSnapshot | null {
  if (!relevantLocation) return null;

  const nearestByCoords = findNearestCityLandmarkByCoords(
    relevantLocation.latitude,
    relevantLocation.longitude
  );
  if (nearestByCoords) {
    return {
      label: nearestByCoords.landmarkName || nearestByCoords.city || 'Plaza 9 de Julio',
      latitude: nearestByCoords.latitude,
      longitude: nearestByCoords.longitude,
    };
  }

  if (relevantLocation.displayName) {
    const landmark = findCityLandmarkByDisplayName(relevantLocation.displayName);
    if (landmark) {
      return {
        label: landmark.landmarkName || landmark.city || 'Plaza 9 de Julio',
        latitude: landmark.latitude,
        longitude: landmark.longitude,
      };
    }

    const fallback = relevantLocation.displayName.split(',')[0]?.trim();
    if (fallback) {
      return {
        label: fallback,
        latitude: relevantLocation.latitude,
        longitude: relevantLocation.longitude,
      };
    }
  }

  return null;
}

function normalizeSearchText(value: string | null | undefined): string {
  return (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

function parseSearchQuery(rawQuery: string | null | undefined): ParsedSearchQuery {
  const raw = rawQuery ?? '';
  const phraseRegex = /"([^"]+)"/g;
  const exactPhrases: string[] = [];
  let match: RegExpExecArray | null;

  while ((match = phraseRegex.exec(raw)) !== null) {
    const normalizedPhrase = normalizeSearchText(match[1]);
    if (normalizedPhrase) exactPhrases.push(normalizedPhrase);
  }

  const rawWithoutPhrases = raw.replace(phraseRegex, ' ');
  const terms = rawWithoutPhrases
    .split(/\s+/)
    .map((term) => normalizeSearchText(term))
    .filter(Boolean);

  const broadTerm = [...exactPhrases, ...terms].join(' ').trim();
  return { exactPhrases, terms, broadTerm };
}

function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
  );
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
      }
    }
  }
  return dp[m][n];
}

function suggestCorrection(tokens: string[], corpus: Map<string, string>): string | null {
  if (corpus.size === 0) return null;
  const corrected: string[] = [];
  let anyChanged = false;
  for (const token of tokens) {
    if (token.length < 3) { corrected.push(token); continue; }
    const maxDist = Math.floor(token.length / 3);
    let best: string | null = null;
    let bestDist = maxDist + 1;
    let bestOriginal: string | null = null;
    for (const [word, original] of corpus) {
      if (Math.abs(word.length - token.length) > maxDist) continue;
      const d = levenshtein(token, word);
      if (d > 0 && d < bestDist) {
        bestDist = d;
        best = word;
        bestOriginal = original;
      }
    }
    if (best && bestOriginal) {
      corrected.push(bestOriginal);
      anyChanged = true;
    } else {
      corrected.push(token);
    }
  }
  return anyChanged ? corrected.join(' ') : null;
}

function matchesParsedQuery(product: SearchProduct, parsed: ParsedSearchQuery): boolean {
  if (parsed.exactPhrases.length === 0 && parsed.terms.length === 0) return true;

  const haystack = normalizeSearchText(
    [
      product.title,
      product.description,
      product.store_name,
      product.username,
    ].join(' ')
  );

  for (const phrase of parsed.exactPhrases) {
    if (!haystack.includes(phrase)) return false;
  }

  for (const term of parsed.terms) {
    if (!haystack.includes(term)) return false;
  }

  return true;
}

export const SearchSidebar: React.FC = () => {
  const { sidebarOpen, closeSidebar, openSidebar } = useSearch();
  const { preferredStoreId, preferredStoreName: ctxPreferredStoreName, setPreferredStoreName } = useCheckout();
  const { profile, session, relevantLocation } = useSession();
  const addressManager = useAddressManager();
  const [isClosing, setIsClosing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const storeCoordsCacheRef = useRef<Map<string, { latitude: number; longitude: number } | null>>(new Map());
  const storeNameCacheRef = useRef<Map<string, string>>(new Map());
  const [subHeaderVisible, setSubHeaderVisible] = useState(true);
  const lastScrollYRef = useRef(0);
  const { headerHeight, isDesktop } = useHeaderOffset();
  const { toast } = useToast();
  const { isFavorited, toggleFavorite } = useFavorites();
  const playFavoriteSound = usePlayFavoriteSound();
  const playBeep = usePlayBeep();
  const vibrate = useVibrate();
  const { setSelectedProduct, setProductSidebarOpen, setProductSidebarFullscreen } = useContentSidebar();
  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [products, setProducts] = useState<SearchProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [cartItems, setCartItems] = useState<Array<{ id: string; product_id: string; quantity: number }>>([]);
  const [updatingProductIds, setUpdatingProductIds] = useState<Set<string>>(new Set());
  const [addingProductIds, setAddingProductIds] = useState<Set<string>>(new Set());
  const [loadingProductId, setLoadingProductId] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('landmark');
  const [gpsError, setGpsError] = useState<string | null>(null);
  const [gpsLocation, setGpsLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [userAddresses, setUserAddresses] = useState<UserAddress[]>([]);
  const [addressesLoaded, setAddressesLoaded] = useState(false);
  const [landmarkSnapshot, setLandmarkSnapshot] = useState<LandmarkSnapshot | null>(() => deriveLandmarkSnapshot(relevantLocation));
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [distanceFilter, setDistanceFilter] = useState<DistanceFilter>('any');
  const [minPriceFilter, setMinPriceFilter] = useState<string>('');
  const [maxPriceFilter, setMaxPriceFilter] = useState<string>('');
  const [conditionFilter, setConditionFilter] = useState<ConditionFilter>('any');
  const [auctionFilter, setAuctionFilter] = useState<AuctionFilter>('any');
  const [dateFilter, setDateFilter] = useState<DateFilter>('any');
  const PAGE_SIZE = 12;
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const [suggestedTerm, setSuggestedTerm] = useState<string | null>(null);
  const wordCorpusRef = useRef<Map<string, string>>(new Map()); // Maps normalized word -> original word
  const corpusSeededRef = useRef(false);
  const skipCorrectionRef = useRef(false);
  const [forceSearchKey, setForceSearchKey] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(() => typeof window !== 'undefined' ? window.innerWidth : 0);
  const [barcodeModalOpen, setBarcodeModalOpen] = useState(false);

  const focusSearchInput = useCallback(() => {
    // Safari iOS blocks programmatic focus unless it happens very close to a user gesture.
    // Strategy: fire an immediate attempt (stays within gesture chain) + retries with
    // increasing delays to cover both the animation settling and Safari's quirks.
    const isSafariMobile = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

    // Avoid iOS auto-focus zoom/jump when opening the sidebar. Users can tap to focus.
    if (isSafariMobile) {
      return () => {};
    }

    const tryFocus = (input: HTMLInputElement) => {
      if (document.activeElement === input) return;
      // readOnly trick: setting then clearing readOnly makes Safari honour the focus() call
      if (isSafariMobile) {
        input.readOnly = false;
      }
      input.focus(isSafariMobile ? undefined : { preventScroll: true });
    };

    // Immediate attempt — keeps us inside the user-gesture window on iOS
    if (inputRef.current) tryFocus(inputRef.current);

    // Scheduled retries: 50 ms, 150 ms, 280 ms, 500 ms
    const delays = isSafariMobile ? [50, 150, 280, 500] : [220, 400];
    const timers = delays.map(delay =>
      window.setTimeout(() => {
        const input = inputRef.current;
        if (input && document.activeElement !== input) tryFocus(input);
      }, delay)
    );

    return () => timers.forEach(t => window.clearTimeout(t));
  }, []);

  useScrollLock(sidebarOpen || isClosing, false, scrollAreaRef);

  useEffect(() => {
    const update = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const getStoreHeaderWidth = useCallback((productCount: number) => {
    const gridColumns = viewportWidth >= 1024 ? 2 : 1;
    const visibleCards = Math.max(1, Math.min(gridColumns, productCount));
    const gapRem = 0.75;
    const totalGridGapRem = (gridColumns - 1) * gapRem;
    const headerGapRem = (visibleCards - 1) * gapRem;
    return {
      width: `calc(((100% - ${totalGridGapRem}rem) / ${gridColumns}) * ${visibleCards} + ${headerGapRem}rem)`,
      maxWidth: '100%',
    };
  }, [viewportWidth]);

  const parsedSearchQuery = useMemo(() => {
    return parseSearchQuery(searchTerm);
  }, [searchTerm]);

  // When a spelling correction is active, classify products using the corrected term's tokens
  const effectiveParsedQuery = useMemo(() => {
    return suggestedTerm ? parseSearchQuery(suggestedTerm) : parsedSearchQuery;
  }, [suggestedTerm, parsedSearchQuery]);

  const commitSearch = useCallback(() => {
    const term = searchInput.trim();
    setSearchTerm(term || undefined);
  }, [searchInput]);

  const activeFilterCount = useMemo(() => {
    return [
      distanceFilter !== 'any',
      minPriceFilter.trim() !== '',
      maxPriceFilter.trim() !== '',
      conditionFilter !== 'any',
      auctionFilter !== 'any',
      dateFilter !== 'any',
    ].filter(Boolean).length;
  }, [auctionFilter, conditionFilter, dateFilter, distanceFilter, maxPriceFilter, minPriceFilter]);

  const loadUserAddresses = useCallback(async () => {
    setAddressesLoaded(false);

    if (!session?.user?.id) {
      setUserAddresses([]);
      setAddressesLoaded(true);
      return;
    }

    try {
      const addresses = await getUserAddresses(session.user.id);
      setUserAddresses(addresses || []);
    } catch {
      setUserAddresses([]);
    } finally {
      setAddressesLoaded(true);
    }
  }, [session?.user?.id]);

  useEffect(() => {
    void loadUserAddresses();
  }, [addressManager.isOpen, loadUserAddresses]);

  useEffect(() => {
    const handleAddressUpdated = () => {
      void loadUserAddresses();
    };

    window.addEventListener('address-updated', handleAddressUpdated);
    return () => {
      window.removeEventListener('address-updated', handleAddressUpdated);
    };
  }, [loadUserAddresses]);

  useEffect(() => {
    const handleSetSearchQuery = (event: CustomEvent) => {
      const query = event.detail?.query;
      const autoSearch = event.detail?.autoSearch;
      if (query) {
        setSearchInput(query);
        // If autoSearch is true, also set the search term to execute the search
        if (autoSearch) {
          setSearchTerm(query);
        }
      }
    };

    window.addEventListener('set-search-query', handleSetSearchQuery as EventListener);
    return () => {
      window.removeEventListener('set-search-query', handleSetSearchQuery as EventListener);
    };
  }, []);

  useEffect(() => {
    // Check URL for ?q= parameter and open sidebar + execute search if present
    const searchParams = new URLSearchParams(window.location.search);
    const queryParam = searchParams.get('q');
    
    if (queryParam) {
      // Open sidebar and set search term
      setSearchInput(queryParam);
      openSidebar();
      // Execute search with autoSearch
      setTimeout(() => {
        setSearchTerm(queryParam);
      }, 100);
    }
  }, [openSidebar]);

  useEffect(() => {
    if (!sidebarOpen) return;
    const cleanup = focusSearchInput();
    return cleanup;
  }, [sidebarOpen, focusSearchInput]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Cmd+K on Mac or Ctrl+K on other systems to open search sidebar
      const isCmdOrCtrl = event.metaKey || event.ctrlKey;
      if (isCmdOrCtrl && event.key === 'k') {
        event.preventDefault();
        // Open the sidebar; the open effect will focus the input.
        openSidebar();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [openSidebar]);

  useEffect(() => {
    const nextSnapshot = deriveLandmarkSnapshot(relevantLocation);
    setLandmarkSnapshot((current) => {
      if (!nextSnapshot) return current;
      if (
        current?.label === nextSnapshot.label &&
        current?.latitude === nextSnapshot.latitude &&
        current?.longitude === nextSnapshot.longitude
      ) {
        return current;
      }
      return nextSnapshot;
    });
  }, [relevantLocation]);

  const primaryUserAddress = userAddresses.find((address) => address.is_primary) ?? userAddresses[0] ?? null;
  const userCanUseHomeMode = Boolean(session?.user?.id);
  const userHasHomeAddress = Boolean(
    primaryUserAddress?.location?.latitude != null && primaryUserAddress?.location?.longitude != null
  );
  const homeButtonLabel = userHasHomeAddress ? 'Casa' : 'Casa: agregar';
  const homeLandmarkLabel = landmarkSnapshot?.label || 'Plaza 9 de Julio';

  useEffect(() => {
    if (!sidebarOpen) return;

    if (!session?.user?.id) {
      setMode('landmark');
      return;
    }

    if (!addressesLoaded) return;

    setMode(userHasHomeAddress ? 'home' : 'landmark');
  }, [addressesLoaded, session?.user?.id, sidebarOpen, userHasHomeAddress]);

  const requestGpsLocation = useCallback(() => {
    setGpsError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGpsLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
      },
      (err) => {
        setGpsError(
          err.code === 1
            ? 'Permiso de ubicación denegado. Tocá "Mi ubicación" para reintentar.'
            : 'No se pudo obtener tu ubicación'
        );
      },
      { enableHighAccuracy: false, timeout: 10000 }
    );
  }, []);

  const handleLandmarkMode = useCallback(() => {
    const nextSnapshot = deriveLandmarkSnapshot(relevantLocation);
    if (nextSnapshot) setLandmarkSnapshot(nextSnapshot);
    setGpsError(null);
    setMode('landmark');
  }, [relevantLocation]);

  const handleHomeMode = useCallback(() => {
    setGpsError(null);
    if (!userCanUseHomeMode) return;
    if (!userHasHomeAddress) {
      addressManager.open({ forceOpen: true });
      return;
    }

    setMode('home');
  }, [addressManager, userCanUseHomeMode, userHasHomeAddress]);

  const handleGpsMode = useCallback(() => {
    setMode('gps');
    requestGpsLocation();
  }, [requestGpsLocation]);

  const referenceCoords = useMemo(() => {
    if (mode === 'home' && userHasHomeAddress) {
      return {
        latitude: primaryUserAddress!.location.latitude,
        longitude: primaryUserAddress!.location.longitude,
      };
    }

    if (mode === 'gps') {
      return gpsLocation;
    }

    if (landmarkSnapshot) {
      return {
        latitude: landmarkSnapshot.latitude,
        longitude: landmarkSnapshot.longitude,
      };
    }

    return null;
  }, [gpsLocation, landmarkSnapshot, mode, primaryUserAddress, userHasHomeAddress]);

  const handleClose = useCallback(() => {
    inputRef.current?.blur();
    setIsClosing(true);
    setTimeout(() => {
      setFiltersOpen(false);
      closeSidebar();
      setIsClosing(false);
    }, 200);
  }, [closeSidebar]);

  const handleOpenStore = useCallback((storeId: string, storeSlug?: string | null) => {
    closeSidebar();
    navigate(`/${storeSlug || storeId}`);
  }, [closeSidebar, navigate]);

  const handleOpenProductSidebar = useCallback((product: SearchProduct) => {
    setSelectedProduct(product as any);
    setProductSidebarFullscreen(true);
    setProductSidebarOpen(true);
  }, [setProductSidebarOpen, setProductSidebarFullscreen, setSelectedProduct]);

  const resetFilters = useCallback(() => {
    setDistanceFilter('any');
    setMinPriceFilter('');
    setMaxPriceFilter('');
    setConditionFilter('any');
    setAuctionFilter('any');
    setDateFilter('any');
  }, []);

  // Load cart items
  const loadCartItems = useCallback(async () => {
    if (session?.user?.id) {
      try {
        const allStoreIds = Array.from(
          new Set(
            products
              .map((product) => product.store_id)
              .filter((storeId): storeId is string => Boolean(storeId))
          )
        );

        if (allStoreIds.length === 0) {
          setCartItems(prev => prev.filter(item => item.id.startsWith('pending-')));
          return;
        }

        const carts = await Promise.all(
          allStoreIds.map((storeId) => getCartForUser(session.user.id, storeId))
        );

        const nextCartItems = carts.flatMap((cart) =>
          ((cart?.cart_items as Array<{ id: string; product_id: string; quantity: number }> | undefined) ?? []).map((item) => ({
            id: item.id,
            product_id: item.product_id,
            quantity: item.quantity,
          }))
        );

        setCartItems(prev => {
          const pendingItems = prev.filter(item => item.id.startsWith('pending-'));
          const pendingNotYetConfirmed = pendingItems.filter(pending =>
            !nextCartItems.some(item => item.product_id === pending.product_id)
          );

          return [...nextCartItems, ...pendingNotYetConfirmed];
        });
      } catch (error) {
        console.error('[SearchSidebar] Error loading DB cart:', error);
      }
    } else {
      const tempItems = getTempCartAsItems();
      setCartItems(
        tempItems.map((item) => ({
          id: item.id,
          product_id: item.product_id,
          quantity: item.quantity ?? 1,
        }))
      );
    }
  }, [products, session?.user?.id]);

  // Sync cart with global changes
  useEffect(() => {
    loadCartItems();

    const handleCartUpdate = () => {
      setTimeout(() => {
        void loadCartItems();
      }, 50);
    };

    window.addEventListener('cart-updated', handleCartUpdate);
    return () => {
      window.removeEventListener('cart-updated', handleCartUpdate);
    };
  }, [loadCartItems]);

  // Build word corpus from product titles
  const addToCorpus = useCallback((items: SearchProduct[]) => {
    for (const item of items) {
      // Extract words with regex: match sequences of letters (with accents) and numbers
      const wordMatches = item.title.match(/[a-záéíóúñüA-ZÁÉÍÓÚÑÜ0-9]+/g) || [];
      
      for (const word of wordMatches) {
        const normalized = normalizeSearchText(word);
        if (normalized.length >= 3) {
          // Prefer storing words with accents over words without accents
          const current = wordCorpusRef.current.get(normalized);
          const currentHasAccents = current ? /[áéíóúñüÁÉÍÓÚÑÜ]/.test(current) : false;
          const wordHasAccents = /[áéíóúñüÁÉÍÓÚÑÜ]/.test(word);
          
          if (!current || (wordHasAccents && !currentHasAccents)) {
            wordCorpusRef.current.set(normalized, word);
          }
        }
      }
    }
  }, []);

  // Search products
  const fetchProducts = useCallback(async () => {
    if (!searchTerm) {
      setProducts([]);
      setSuggestedTerm(null);
      setLoading(false);
      return;
    }

    const backendSearchTerm = parsedSearchQuery.broadTerm || searchTerm;
    
    if (!backendSearchTerm) {
      setProducts([]);
      setSuggestedTerm(null);
      setLoading(false);
      return;
    }

    setSuggestedTerm(null);
    setLoading(true);

    const normalizeResults = async (results: any[]): Promise<SearchProduct[]> => {
      const storeIds = Array.from(
        new Set(results.map((r: any) => r.store_id).filter((id): id is string => Boolean(id)))
      );
      const missingStoreIds = storeIds.filter((id) => !storeCoordsCacheRef.current.has(id));
      if (missingStoreIds.length > 0) {
        const entries = await Promise.all(
          missingStoreIds.map(async (storeId) => {
            try {
              const addresses = await getStoreAddresses(storeId);
              const primaryAddress = addresses.find((a) => a.is_primary) ?? addresses[0] ?? null;
              return [
                storeId,
                primaryAddress?.location?.latitude != null && primaryAddress?.location?.longitude != null
                  ? { latitude: primaryAddress.location.latitude, longitude: primaryAddress.location.longitude }
                  : null,
              ] as const;
            } catch {
              return [storeId, null] as const;
            }
          })
        );
        entries.forEach(([id, coords]) => storeCoordsCacheRef.current.set(id, coords));
      }
      return results
        .filter((r: any) => Boolean(r.store_id))
        .map((r: any) => {
          const addressCoords = storeCoordsCacheRef.current.get(r.store_id);
          const storeName = r.stores?.name || 'Tienda';
          storeNameCacheRef.current.set(r.store_id, storeName);
          return {
            id: r.id,
            title: r.title,
            description: r.description ?? '',
            fixed_sale_price: r.fixed_sale_price ?? 0,
            created_at: r.created_at ?? undefined,
            cover_url: r.cover_url ?? undefined,
            main_image_url: r.main_image_url ?? undefined,
            image_urls: r.image_urls ?? [],
            store_id: r.store_id,
            store_name: storeName,
            store_slug: r.stores?.slug ?? null,
            store_latitude: addressCoords?.latitude ?? r.stores?.latitude ?? null,
            store_longitude: addressCoords?.longitude ?? r.stores?.longitude ?? null,
            condition: r.condition ?? null,
            is_auction: r.is_auction ?? false,
            username: r.username ?? undefined,
            stock: r.stock ?? null,
          };
        });
    };

    try {
      const rawResults = await searchProductsPaginated(backendSearchTerm, 0, PAGE_SIZE, undefined, undefined, undefined);
      const normalizedResults = await normalizeResults(rawResults);
      addToCorpus(normalizedResults);
      const strictResults = normalizedResults.filter((p) => matchesParsedQuery(p, parsedSearchQuery));

      if (strictResults.length > 0) {
        setProducts(strictResults);
        setHasMore(rawResults.length === PAGE_SIZE);
      } else {
        // Seed corpus with recent products if empty
        if (!corpusSeededRef.current) {
          corpusSeededRef.current = true;
          try {
            const seedResults = await searchProductsPaginated(null, 0, 100, undefined, undefined, undefined);
            const seedNormalized = await normalizeResults(seedResults);
            addToCorpus(seedNormalized);
          } catch (err) {
            console.error('[SearchSidebar] Error seeding corpus:', err);
          }
        }

        // Try to find a spelling correction
        const tokens = parsedSearchQuery.terms.length > 0
          ? parsedSearchQuery.terms
          : normalizeSearchText(backendSearchTerm).split(/\s+/).filter(Boolean);
        const suggestion = skipCorrectionRef.current ? null : suggestCorrection(tokens, wordCorpusRef.current);
        skipCorrectionRef.current = false;

        if (suggestion && suggestion !== normalizeSearchText(backendSearchTerm)) {
          // Fetch results for the suggested term
          try {
            const suggestionRaw = await searchProductsPaginated(suggestion, 0, PAGE_SIZE, undefined, undefined, undefined);
            const suggestionNormalized = await normalizeResults(suggestionRaw);
            addToCorpus(suggestionNormalized);
            setSuggestedTerm(suggestion); // always mark the suggestion, even if 0 results
            if (suggestionNormalized.length > 0) {
              setProducts(suggestionNormalized);
              setHasMore(suggestionRaw.length === PAGE_SIZE);
            } else {
              setProducts([]);
            }
          } catch (err) {
            console.error('[SearchSidebar] Error fetching suggestion:', err);
            setProducts([]);
          }
        } else {
          setProducts([]);
        }
      }
    } catch (error) {
      console.error('[SearchSidebar] Error searching products:', error);
      toast({
        title: 'Error de búsqueda',
        description: 'No se pudieron cargar los resultados',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [addToCorpus, forceSearchKey, parsedSearchQuery, searchTerm, toast]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Pull-to-close
  usePullToClose(scrollAreaRef, {
    onClose: handleClose,
    threshold: 80,
    enabled: sidebarOpen && !isDesktop
  });

  // ESC to close + focus trap (Tab) + block arrow keys from reaching background
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!sidebarOpen) return;

      // Close on Escape
      if (event.key === 'Escape') {
        const inputFocused = document.activeElement === inputRef.current;

        if (inputFocused) {
          event.preventDefault();
          if (searchInput.trim() !== '' || searchTerm) {
            setSearchInput('');
            setSearchTerm(undefined);
            setSuggestedTerm(null);
            setProducts([]);
            setHasMore(true);
            return;
          }

          handleClose();
          return;
        }

        event.preventDefault();
        if (filtersOpen) {
          setFiltersOpen(false);
          return;
        }
        handleClose();
        return;
      }

      // Trap Tab within the sidebar
      if (event.key === 'Tab' && sidebarRef.current) {
        const focusable = sidebarRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!focusable.length) { event.preventDefault(); return; }
        if (event.shiftKey) {
          if (document.activeElement === first) {
            event.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
      }
    };

    if (sidebarOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [sidebarOpen, handleClose, filtersOpen, searchInput, searchTerm]);

  // Sub-header hide on scroll down / show on scroll up
  useEffect(() => {
    if (!sidebarOpen) return;
    // Find the scrollable viewport inside ScrollArea
    const container = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement | null;
    if (!container) return;
    const handleScroll = () => {
      const current = container.scrollTop;
      const diff = current - lastScrollYRef.current;
      if (diff > 8) {
        setSubHeaderVisible(false);
      } else if (diff < -8) {
        setSubHeaderVisible(true);
      }
      lastScrollYRef.current = current;
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [sidebarOpen]);

  // Get cart item by product ID
  const getCartItemByProductId = useCallback((productId: string) => {
    return cartItems.find(item => item.product_id === productId) ?? null;
  }, [cartItems]);

  // Check if product is in cart
  // Add to cart
  const handleAddToCart = useCallback(async (productId: string) => {
    const product = products.find((item) => item.id === productId);
    if (!product) {
      toast({
        title: 'Error',
        description: 'No pudimos encontrar el producto para agregarlo al canasto',
        variant: 'destructive',
      });
      return;
    }

    setAddingProductIds(prev => new Set([...prev, productId]));

    setCartItems(prev => {
      const exists = prev.some(item => item.product_id === productId);
      if (!exists) {
        return [...prev, { id: `pending-${productId}`, product_id: productId, quantity: 1 }];
      }
      return prev;
    });

    try {
      if (session?.user?.id) {
        const cart = await getOrCreateCartForStore(session.user.id, product.store_id);
        const addedItem = await addCartItem(session.user.id, productId, 1, cart.id);
        setCartItems(prev => [
          ...prev.filter(item => item.product_id !== productId),
          {
            id: addedItem.id,
            product_id: addedItem.product_id,
            quantity: addedItem.quantity,
          },
        ]);
      } else {
        const fullProduct = await getProductById(productId);
        if (!fullProduct) {
          throw new Error('Producto no encontrado');
        }

        addToTempCart({
          id: fullProduct.id,
          title: fullProduct.title,
          fixed_sale_price: fullProduct.fixed_sale_price ?? product.fixed_sale_price ?? 0,
          currency: 'ARS' as const,
          stock: fullProduct.stock || 999,
        }, 1, product.store_id);

        const tempCartItem = getTempCartAsItems(product.store_id).find((item) => item.product_id === productId);
        if (tempCartItem) {
          setCartItems(prev => [
            ...prev.filter(item => item.product_id !== productId),
            {
              id: tempCartItem.id,
              product_id: tempCartItem.product_id,
              quantity: tempCartItem.quantity,
            },
          ]);
        }
      }

      window.dispatchEvent(new CustomEvent('cart-updated'));
    } catch (error) {
      console.error('[SearchSidebar] Error adding to cart:', error);
      setCartItems(prev => prev.filter(item => item.id !== `pending-${productId}`));
      toast({
        title: 'Error',
        description: (error as any)?.message || 'No pudimos agregar el producto al canasto',
        variant: 'destructive',
      });
    } finally {
      setAddingProductIds(prev => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  }, [products, session?.user?.id, toast]);

  // Update quantity
  const handleUpdateQuantity = useCallback(async (
    productId: string,
    cartItemId: string,
    quantityCalculator: (current: number) => number
  ) => {
    const cartItem = getCartItemByProductId(productId);
    if (!cartItem) return;

    if (session?.user?.id && cartItemId.startsWith('pending-')) {
      await loadCartItems();
      return;
    }

    const nextQuantity = quantityCalculator(cartItem.quantity);
    setUpdatingProductIds(prev => new Set(prev).add(productId));

    try {
      if (nextQuantity <= 0) {
        if (session?.user?.id && !cartItemId.startsWith('pending-')) {
          await removeCartItem(cartItemId);
        } else {
          removeFromTempCart(productId);
        }
        setCartItems(prev => prev.filter(item => item.product_id !== productId));
      } else {
        if (session?.user?.id) {
          if (!cartItemId.startsWith('pending-')) {
            await updateCartItem(cartItemId, nextQuantity);
          }
        } else {
          updateTempCartItemQuantity(productId, nextQuantity);
        }

        setCartItems(prev => prev.map(item =>
          item.product_id === productId
            ? { ...item, quantity: nextQuantity }
            : item
        ));
      }

      window.dispatchEvent(new CustomEvent('cart-updated'));
    } catch (err) {
      console.error('[SearchSidebar] Error updating quantity:', err);
      toast({
        title: 'Error',
        description: 'No pudimos actualizar la cantidad',
        variant: 'destructive',
      });
    } finally {
      setUpdatingProductIds(prev => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  }, [getCartItemByProductId, loadCartItems, session?.user?.id, toast]);

  useEffect(() => {
    setAddingProductIds(prev => {
      const next = new Set(prev);
      for (const productId of prev) {
        if (cartItems.some(item => item.product_id === productId)) {
          next.delete(productId);
        }
      }
      return next;
    });
  }, [cartItems]);

  const filteredProducts = useMemo(() => {
    const minPrice = minPriceFilter.trim() === '' ? null : Number(minPriceFilter);
    const maxPrice = maxPriceFilter.trim() === '' ? null : Number(maxPriceFilter);
    const now = Date.now();

    return products.filter((product) => {
      const productPrice = Number(product.fixed_sale_price ?? 0) || 0;

      if (distanceFilter !== 'any' && referenceCoords && product.store_latitude != null && product.store_longitude != null) {
        const distanceKm = haversineKm(
          referenceCoords.latitude,
          referenceCoords.longitude,
          product.store_latitude,
          product.store_longitude
        );
        if (distanceKm > Number(distanceFilter)) return false;
      }

      if (minPrice != null && Number.isFinite(minPrice) && productPrice < minPrice) return false;
      if (maxPrice != null && Number.isFinite(maxPrice) && productPrice > maxPrice) return false;
      if (conditionFilter !== 'any' && (product.condition ?? null) !== conditionFilter) return false;
      if (auctionFilter === 'auction' && !product.is_auction) return false;
      if (auctionFilter === 'fixed' && product.is_auction) return false;

      if (dateFilter !== 'any' && product.created_at) {
        const createdAt = new Date(product.created_at).getTime();
        let cutoff = 0;
        if (dateFilter === '24h') cutoff = now - 24 * 60 * 60 * 1000;
        if (dateFilter === '7d') cutoff = now - 7 * 24 * 60 * 60 * 1000;
        if (dateFilter === '30d') cutoff = now - 30 * 24 * 60 * 60 * 1000;
        if (dateFilter === '365d') cutoff = now - 365 * 24 * 60 * 60 * 1000;
        if (cutoff && createdAt < cutoff) return false;
      }

      return true;
    });
  }, [auctionFilter, conditionFilter, dateFilter, distanceFilter, maxPriceFilter, minPriceFilter, products, referenceCoords]);

  // Group products by store
  const groupedProducts: GroupedProducts = useMemo(() => {
    return filteredProducts.reduce((acc, product) => {
      const storeId = product.store_id || 'default';
      const storeName = product.store_name || 'Tienda';
      const hasStoreCoords =
        referenceCoords &&
        product.store_latitude != null &&
        product.store_longitude != null;
      const distanceKm = hasStoreCoords
        ? haversineKm(
            referenceCoords.latitude,
            referenceCoords.longitude,
            product.store_latitude as number,
            product.store_longitude as number
          )
        : null;

      if (!acc[storeId]) {
        acc[storeId] = {
          store_name: storeName,
          store_slug: product.store_slug ?? null,
          store_latitude: product.store_latitude ?? null,
          store_longitude: product.store_longitude ?? null,
          distanceKm,
          distanceLabel: distanceKm != null ? formatDistance(distanceKm) : null,
          products: []
        };
      }

      acc[storeId].products.push(product);
      return acc;
    }, {} as GroupedProducts);
  }, [filteredProducts, referenceCoords]);

  /** Returns true if the product title matches the search query terms */
  const productTitleMatchesSearch = useCallback((product: SearchProduct): boolean => {
    if (!effectiveParsedQuery.terms.length && !effectiveParsedQuery.exactPhrases.length) return true;
    const titleNorm = normalizeSearchText(product.title);
    for (const phrase of effectiveParsedQuery.exactPhrases) {
      if (!titleNorm.includes(phrase)) return false;
    }
    for (const term of effectiveParsedQuery.terms) {
      if (!titleNorm.includes(term)) return false;
    }
    return true;
  }, [effectiveParsedQuery]);

  const groupEntries = useMemo(() => {
    const distanceSorted = Object.entries(groupedProducts).sort(([, a], [, b]) => {
      const aDistance = a.distanceKm ?? Number.POSITIVE_INFINITY;
      const bDistance = b.distanceKm ?? Number.POSITIVE_INFINITY;
      if (aDistance !== bDistance) return aDistance - bDistance;
      return a.store_name.localeCompare(b.store_name, 'es');
    });

    // Tag each group: does any product match by title?
    const tagged = distanceSorted.map(([storeId, data]) => ({
      storeId,
      data,
      hasTitleMatch: data.products.some((p) => productTitleMatchesSearch(p)),
    }));

    const titleGroups = tagged.filter((g) => g.hasTitleMatch).map((g) => [g.storeId, g.data] as [string, typeof g.data]);
    const relatedGroups = tagged.filter((g) => !g.hasTitleMatch).map((g) => [g.storeId, g.data] as [string, typeof g.data]);

    // Move preferred store to top of title groups (or top overall if it is a related group)
    const reorder = (list: [string, (typeof tagged)[0]['data']][]): [string, (typeof tagged)[0]['data']][] => {
      if (!preferredStoreId || list.length <= 1) return list;
      const idx = list.findIndex(([id]) => id === preferredStoreId);
      if (idx <= 0) return list;
      const next = [...list];
      const [preferred] = next.splice(idx, 1);
      next.unshift(preferred);
      return next;
    };

    return {
      titleGroups: reorder(titleGroups),
      relatedGroups,
      all: [...reorder(titleGroups), ...relatedGroups],
    };
  }, [groupedProducts, preferredStoreId, productTitleMatchesSearch]);
  const hasPreferredStoreResults = useMemo(() => {
    if (!preferredStoreId) return false;
    return groupEntries.all.some(([storeId]) => storeId === preferredStoreId);
  }, [groupEntries.all, preferredStoreId]);
  const shouldShowNearbyStoresNotice = useMemo(() => {
    return groupEntries.all.length > 1;
  }, [groupEntries.all]);
  const hasSearchTerm = useMemo(() => {
    return Boolean(searchTerm);
  }, [searchTerm]);
  const shouldShowPreferredStoreMissingNotice = useMemo(() => {
    return hasSearchTerm && Boolean(preferredStoreId) && groupEntries.all.length > 0 && !hasPreferredStoreResults;
  }, [groupEntries.all.length, hasPreferredStoreResults, hasSearchTerm, preferredStoreId]);
  const preferredStoreName = useMemo(() => {
    if (!preferredStoreId) return 'Tu tienda preferida';
    if (ctxPreferredStoreName) return ctxPreferredStoreName;
    return storeNameCacheRef.current.get(preferredStoreId) || 'Tu tienda preferida';
  }, [preferredStoreId, ctxPreferredStoreName]);
  const controlGroupClassName = 'inline-flex items-center rounded-full border border-border bg-background p-1 shadow-sm';
  const desktopControlButtonClassName = 'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold transition-colors whitespace-nowrap';
  const mobileControlButtonClassName = 'flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold transition-colors whitespace-nowrap';

  return (
    <>
      <AnimatePresence>
        {(sidebarOpen || isClosing) && (
          <>
            {/* Overlay */}
            <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-transparent z-[10000]"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) {
                e.stopPropagation();
                handleClose();
              }
            }}
            onTouchStart={(e) => {
              if (e.target === e.currentTarget) {
                e.stopPropagation();
                handleClose();
              }
            }}
          />

          {/* Sidebar */}
          <motion.div
            ref={sidebarRef}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.2 }}
            onKeyDown={(e) => e.stopPropagation()}
            className="fixed right-0 w-full bg-background shadow-2xl z-[10001] flex flex-col overflow-x-hidden"
            style={{
              top: 0,
              bottom: 0,
              height: '100dvh',
            }}
          >
            {/* Safe Area Spacer for Mobile */}
            {!isDesktop && <div className="h-[env(safe-area-inset-top,0px)] bg-primary w-full shrink-0" />}

            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b shrink-0 bg-primary text-primary-foreground min-h-[64px]">
              <div
                className="min-w-0 flex flex-1 items-center gap-2 cursor-text"
                onClick={() => inputRef.current?.focus()}
              >
                <SearchIcon className="w-5 h-5 text-primary-foreground shrink-0" />
                <Input
                  id="sidebar-search-input"
                  ref={inputRef}
                  placeholder="Buscar en Minibid..."
                  value={searchInput}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSearchInput(val);
                    if (val.trim() === '') {
                      setSearchTerm(undefined);
                      setProducts([]);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      e.stopPropagation();
                      commitSearch();
                      return;
                    }

                    if (e.key === 'Escape') {
                      e.preventDefault();
                      e.stopPropagation();

                      if (searchInput.trim() !== '' || searchTerm) {
                        setSearchInput('');
                        setSearchTerm(undefined);
                        setSuggestedTerm(null);
                        setProducts([]);
                        setHasMore(true);
                        return;
                      }

                      handleClose();
                    }
                  }}
                  className="search-header-input-reset h-auto border-0 bg-transparent p-0 text-[16px] sm:text-lg font-semibold text-primary-foreground caret-white placeholder:text-primary-foreground/70 shadow-none outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
                />
              </div>
              <div className="flex items-center gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setBarcodeModalOpen(true);
                        }}
                        className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-2.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-white/15 sm:px-3"
                      >
                        <ScanBarcode className="h-4 w-4" />
                        <span className="hidden sm:inline">Escáner</span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent variant="sidebar">
                      <p>Escanear código de barras</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setFiltersOpen(true);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-white/15"
                >
                  <Settings className="h-4 w-4" />
                  <span>Filtros</span>
                  {activeFilterCount > 0 && (
                    <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-white text-primary px-1.5 text-[11px] font-bold">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleClose(); }}
                        className="p-1 hover:bg-white/10 text-white rounded-lg transition-colors hover:text-white"
                      >
                        <X className="w-5 h-5 text-white" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent variant="sidebar">
                      <p>Cerrar</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            {/* Search Input */}
            <motion.div
              initial={false}
              animate={{ height: subHeaderVisible ? 'auto' : 0, opacity: subHeaderVisible ? 1 : 0 }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              className="shrink-0 overflow-hidden"
            >
            <div className="p-3 border-b space-y-2.5">
              <div className="rounded-xl border border-primary/20 bg-primary/5 p-2.5 shadow-sm space-y-2.5">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-3.5 w-3.5 text-primary" />
                    <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Ubicación base</p>
                  </div>

                  <div className="hidden md:flex items-center gap-2 overflow-x-auto pb-0.5">
                    <div className={controlGroupClassName}>
                      <button
                        onClick={handleLandmarkMode}
                        className={cn(desktopControlButtonClassName, mode === 'landmark' ? 'bg-primary text-primary-foreground shadow-sm shadow-primary/20' : 'text-muted-foreground hover:text-foreground')}
                      >
                        <MapPin className="h-3.5 w-3.5 shrink-0" />
                        <span className="max-w-[13rem] truncate">{homeLandmarkLabel}</span>
                      </button>
                      {userCanUseHomeMode && (
                        <button
                          onClick={handleHomeMode}
                          className={cn(
                            desktopControlButtonClassName,
                            mode === 'home'
                              ? 'bg-primary text-primary-foreground shadow-sm shadow-primary/20'
                              : userHasHomeAddress
                                ? 'text-muted-foreground hover:text-foreground'
                                : 'text-primary border border-primary/20 bg-primary/5 hover:bg-primary/10'
                          )}
                        >
                          <Home className="h-3.5 w-3.5" />
                          {homeButtonLabel}
                        </button>
                      )}
                      <button
                        onClick={handleGpsMode}
                        className={cn(desktopControlButtonClassName, mode === 'gps' ? 'bg-primary text-primary-foreground shadow-sm shadow-primary/20' : 'text-muted-foreground hover:text-foreground')}
                      >
                        <LocateFixed className="h-3.5 w-3.5" />
                        Mi ubicación
                      </button>
                    </div>
                  </div>

                  <div className="flex md:hidden items-center gap-2 overflow-x-auto pb-0.5 scrollbar-hide flex-nowrap">
                    <div className={cn(controlGroupClassName, 'flex-shrink-0')}>
                      <button
                        onClick={handleLandmarkMode}
                        className={cn(mobileControlButtonClassName, mode === 'landmark' ? 'bg-primary text-primary-foreground shadow-sm shadow-primary/20' : 'text-muted-foreground')}
                      >
                        <MapPin className="h-3.5 w-3.5 shrink-0" />
                        <span className="max-w-[8.5rem] truncate">{homeLandmarkLabel}</span>
                      </button>
                      {userCanUseHomeMode && (
                        <button
                          onClick={handleHomeMode}
                          className={cn(
                            mobileControlButtonClassName,
                            mode === 'home'
                              ? 'bg-primary text-primary-foreground shadow-sm shadow-primary/20'
                              : userHasHomeAddress
                                ? 'text-muted-foreground'
                                : 'text-primary border border-primary/20 bg-primary/5'
                          )}
                        >
                          <Home className="h-3.5 w-3.5" />
                          {homeButtonLabel}
                        </button>
                      )}
                      <button
                        onClick={handleGpsMode}
                        className={cn(mobileControlButtonClassName, mode === 'gps' ? 'bg-primary text-primary-foreground shadow-sm shadow-primary/20' : 'text-muted-foreground')}
                      >
                        <LocateFixed className="h-3.5 w-3.5" />
                        Mi ubicación
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {gpsError && (
                <div className="rounded-xl border border-destructive/20 bg-destructive/5 px-3 py-2 text-sm text-destructive shadow-sm">
                  {gpsError}
                </div>
              )}

              {activeFilterCount > 0 && (
                <div className="flex flex-wrap items-center gap-2 rounded-xl border border-primary/20 bg-primary/5 px-3 py-2 text-xs shadow-sm">
                  <span className="font-semibold text-foreground">Filtros activos:</span>
                  {distanceFilter !== 'any' && <span className="rounded-full bg-background px-2 py-1">Hasta {distanceFilter} km</span>}
                  {minPriceFilter.trim() !== '' && <span className="rounded-full bg-background px-2 py-1">Min ${minPriceFilter}</span>}
                  {maxPriceFilter.trim() !== '' && <span className="rounded-full bg-background px-2 py-1">Max ${maxPriceFilter}</span>}
                  {conditionFilter !== 'any' && <span className="rounded-full bg-background px-2 py-1">{translateCondition(conditionFilter)}</span>}
                  {auctionFilter === 'auction' && <span className="rounded-full bg-background px-2 py-1">Sólo remate</span>}
                  {auctionFilter === 'fixed' && <span className="rounded-full bg-background px-2 py-1">Sin remate</span>}
                  {dateFilter !== 'any' && <span className="rounded-full bg-background px-2 py-1">Publicación reciente</span>}
                </div>
              )}
            </div>
            </motion.div>

            {/* Content */}
            <ScrollArea ref={scrollAreaRef} className="flex-1 overflow-x-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="p-4 space-y-6" onClick={(e) => e.stopPropagation()}>
                {loading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-24 bg-muted rounded-xl animate-pulse" />
                    ))}
                  </div>
                ) : !searchTerm || groupEntries.all.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <SearchIcon className="h-12 w-12 text-muted-foreground/30 mb-3" />
                    <p className="text-muted-foreground font-medium">
                      {searchTerm ? 'Sin resultados' : 'Comienza a buscar'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {searchTerm ? 'No hay productos que coincidan' : 'Escribe algo para buscar'}
                    </p>
                    {searchTerm && suggestedTerm && (
                      <button
                        type="button"
                        onClick={() => {
                          setSearchInput(suggestedTerm);
                          setSuggestedTerm(null);
                          setSearchTerm(suggestedTerm);
                        }}
                        className="mt-3 text-sm text-primary underline hover:text-primary/80 transition-colors"
                      >
                        ¿Quisiste decir <span className="font-semibold italic">{suggestedTerm}</span>?
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    {suggestedTerm && (
                      <div className="rounded-xl border border-primary/20 bg-primary/5 px-3 py-2 text-sm shadow-sm">
                        <p className="text-foreground">
                          Mostrando resultados para{' '}
                          <span className="font-semibold text-primary italic">{suggestedTerm}</span>
                        </p>
                        <button
                          type="button"
                          onClick={() => { skipCorrectionRef.current = true; setSuggestedTerm(null); setProducts([]); setForceSearchKey(k => k + 1); }}
                          className="text-xs text-muted-foreground underline hover:text-foreground transition-colors mt-0.5"
                        >
                          Buscar &ldquo;{searchTerm}&rdquo;
                        </button>
                      </div>
                    )}
                    {shouldShowPreferredStoreMissingNotice && (
                      <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
                        {preferredStoreName} no tiene productos con este término. Mostramos resultados de otras tiendas cercanas.
                      </div>
                    )}
                    {groupEntries.titleGroups.map(([storeId, storeData], index) => (
                      <React.Fragment key={storeId}>
                      {index === 1 && hasPreferredStoreResults && (
                        <div className="rounded-xl border border-primary/25 bg-primary/10 px-3 py-2 text-xs text-primary text-center">
                          Resultados en otras tiendas
                        </div>
                      )}
                      <div className="space-y-3">
                      {/* Store Header */}
                      <div
                        className="flex items-center justify-between gap-2 px-3 py-2 bg-muted/50 rounded-lg border border-border/50"
                        style={getStoreHeaderWidth(storeData.products.length)}
                      >
                        <div className="flex items-center gap-2 flex-1">
                          <StoreIcon className="h-4 w-4 text-primary" />
                          <div className="min-w-0 flex-1">
                            <button
                              type="button"
                              onClick={() => handleOpenStore(storeId, storeData.store_slug)}
                              className="text-sm font-semibold text-foreground truncate hover:text-primary hover:underline text-left"
                              title={`Abrir tienda ${storeData.store_name}`}
                            >
                              {storeData.store_name}
                            </button>
                          </div>
                          <div className="flex items-center gap-2 ml-auto">
                            {storeData.distanceLabel && (
                              <span className="text-xs text-primary bg-primary/10 px-2 py-1 rounded whitespace-nowrap">
                                {storeData.distanceLabel}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Products Grid */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {storeData.products.map((product) => (
                          <div 
                            key={product.id}
                            className="bg-card rounded-xl border border-border/50 p-3 flex gap-3 group hover:border-primary/30 transition-all shadow-sm hover:shadow-md cursor-pointer"
                            role="button"
                            tabIndex={0}
                            onClick={() => handleOpenProductSidebar(product)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                handleOpenProductSidebar(product);
                              }
                            }}
                          >
                            {/* Product Image */}
                            <div
                              className="h-20 w-20 rounded-lg bg-primary/10 flex-shrink-0 relative overflow-hidden flex items-center justify-center cursor-pointer"
                              role="button"
                              tabIndex={0}
                              onClick={() => handleOpenProductSidebar(product)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  handleOpenProductSidebar(product);
                                }
                              }}
                              title={`Abrir ${product.title}`}
                            >
                              {product.main_image_url || product.cover_url || (Array.isArray(product.image_urls) && product.image_urls[0]) ? (
                                <img src={product.main_image_url || product.cover_url || (Array.isArray(product.image_urls) && product.image_urls[0]) || ''} alt={product.title} className="w-full h-full object-cover" />
                              ) : (
                                <ShoppingCart className="w-10 h-10 text-primary/30" />
                              )}
                              
                              {/* Heart Toggle Button */}
                              <div className="absolute top-1 right-1 z-10">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const wasNotFavorited = !isFavorited(product.id);
                                    toggleFavorite({
                                      id: product.id,
                                      title: product.title,
                                      description: product.description,
                                      fixed_sale_price: product.fixed_sale_price,
                                      stock: 999,
                                      addedAt: Date.now(),
                                      store_id: storeId,
                                      store_name: storeData.store_name,
                                    });
                                    if (wasNotFavorited) {
                                      playFavoriteSound();
                                      vibrate(100);
                                    }
                                  }}
                                  className="h-10 w-10 p-0 rounded-full shadow-md hover:shadow-lg transition-all active:scale-75 duration-75 bg-white/80 dark:bg-gray-900/80 hover:bg-white dark:hover:bg-gray-800 border-0 flex items-center justify-center shrink-0 aspect-square"
                                  title="Agregar a favoritos"
                                >
                                  <Heart 
                                    className={cn(
                                      "h-5 w-5 transition-all",
                                      isFavorited(product.id) ? "fill-red-500 text-red-500" : "text-muted-foreground"
                                    )}
                                  />
                                </button>
                              </div>
                            </div>

                            {/* Product Info */}
                            <div className="flex-1 min-w-0 flex flex-col justify-between">
                              <div>
                                <button
                                  onClick={() => handleOpenProductSidebar(product)}
                                  className="block w-full min-w-0 text-sm font-medium text-foreground whitespace-normal break-words leading-snug group-hover:text-primary transition-colors hover:underline text-left cursor-pointer"
                                >
                                  {product.title}
                                </button>
                                {product.description && (
                                  <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                                    {stripHtml(product.description)}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center justify-between mt-2 gap-2">
                                <div className="flex flex-col gap-0.5">
                                  <span className="text-sm font-bold text-primary">
                                    {formatCurrency(product.fixed_sale_price || 0)}
                                  </span>
                                  {getCartItemByProductId(product.id) && (
                                    <span className="inline-flex items-center gap-1 w-fit px-1.5 py-0.5 rounded-full bg-primary/15 text-primary text-[10px] font-semibold leading-none">
                                      {addingProductIds.has(product.id) ? (
                                        <>
                                          <Loader2 className="w-2.5 h-2.5 shrink-0 animate-spin" />
                                          Agregando
                                        </>
                                      ) : (
                                        <>
                                          <ShoppingCart className="w-2.5 h-2.5 shrink-0" />
                                          Agregado
                                        </>
                                      )}
                                    </span>
                                  )}
                                </div>
                                {/* Right: fixed-height container prevents shift */}
                                <div className="h-[43px] flex items-center shrink-0" onClick={(e) => e.stopPropagation()}>
                                  {getCartItemByProductId(product.id) ? (
                                    <QuantitySelector
                                      cartItemId={getCartItemByProductId(product.id)?.id || ''}
                                      quantity={getCartItemByProductId(product.id)?.quantity || 1}
                                      onUpdateQuantity={(cartItemId, quantityCalculator) => {
                                        void handleUpdateQuantity(product.id, cartItemId, quantityCalculator);
                                      }}
                                      onPlayBeep={playBeep}
                                      onVibrate={vibrate}
                                      disabled={
                                        updatingProductIds.has(product.id) ||
                                        Boolean(getCartItemByProductId(product.id)?.id?.startsWith('pending-'))
                                      }
                                    />
                                  ) : (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleAddToCart(product.id); }}
                                      className="h-[43px] w-[43px] p-0 rounded-full bg-primary text-white shadow-lg hover:shadow-xl transition-all active:scale-75 duration-75 flex items-center justify-center shrink-0 aspect-square"
                                      title="Agregar al canasto (1 unidad)"
                                    >
                                      <Plus className="h-6 w-6" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                      </React.Fragment>
                    ))}
                    {groupEntries.relatedGroups.length > 0 && groupEntries.titleGroups.length > 0 && (
                      <>
                        <div className="rounded-xl border border-primary/25 bg-primary/10 px-3 py-2 text-xs text-primary text-center">
                          También encontramos &ldquo;{suggestedTerm ?? searchTerm}&rdquo; en descripciones de productos de otras tiendas
                        </div>
                        {groupEntries.relatedGroups.map(([storeId, storeData]) => (
                          <React.Fragment key={storeId}>
                          <div className="space-y-3">
                          {/* Store Header */}
                          <div
                            className="flex items-center justify-between gap-2 px-3 py-2 bg-muted/50 rounded-lg border border-border/50"
                            style={getStoreHeaderWidth(storeData.products.length)}
                          >
                            <div className="flex items-center gap-2 flex-1">
                              <StoreIcon className="h-4 w-4 text-muted-foreground" />
                              <div className="min-w-0 flex-1">
                                <button
                                  type="button"
                                  onClick={() => handleOpenStore(storeId, storeData.store_slug)}
                                  className="text-sm font-semibold text-foreground truncate hover:text-primary hover:underline text-left"
                                  title={`Abrir tienda ${storeData.store_name}`}
                                >
                                  {storeData.store_name}
                                </button>
                              </div>
                              <div className="flex items-center gap-2 ml-auto">
                                {storeData.distanceLabel && (
                                  <span className="text-xs text-muted-foreground bg-background px-2 py-1 rounded whitespace-nowrap">
                                    {storeData.distanceLabel}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Products Grid */}
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                            {storeData.products.map((product) => (
                              <div 
                                key={product.id}
                                className="bg-card rounded-xl border border-border/50 p-3 flex gap-3 group hover:border-border transition-all shadow-sm hover:shadow-md cursor-pointer opacity-90"
                                role="button"
                                tabIndex={0}
                                onClick={() => handleOpenProductSidebar(product)}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    handleOpenProductSidebar(product);
                                  }
                                }}
                              >
                                {/* Product Image */}
                                <div
                                  className="h-20 w-20 rounded-lg bg-muted flex-shrink-0 relative overflow-hidden flex items-center justify-center cursor-pointer"
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => handleOpenProductSidebar(product)}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                      event.preventDefault();
                                      handleOpenProductSidebar(product);
                                    }
                                  }}
                                  title={`Abrir ${product.title}`}
                                >
                                  {product.main_image_url || product.cover_url || (Array.isArray(product.image_urls) && product.image_urls[0]) ? (
                                    <img src={product.main_image_url || product.cover_url || (Array.isArray(product.image_urls) && product.image_urls[0]) || ''} alt={product.title} className="w-full h-full object-cover" />
                                  ) : (
                                    <ShoppingCart className="w-10 h-10 text-muted-foreground/30" />
                                  )}
                                  <div className="absolute top-1 right-1 z-10">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const wasNotFavorited = !isFavorited(product.id);
                                        toggleFavorite({
                                          id: product.id,
                                          title: product.title,
                                          description: product.description,
                                          fixed_sale_price: product.fixed_sale_price,
                                          stock: 999,
                                          addedAt: Date.now(),
                                          store_id: storeId,
                                          store_name: storeData.store_name,
                                        });
                                        if (wasNotFavorited) {
                                          playFavoriteSound();
                                          vibrate(100);
                                        }
                                      }}
                                      className="h-10 w-10 p-0 rounded-full shadow-md hover:shadow-lg transition-all active:scale-75 duration-75 bg-white/80 dark:bg-gray-900/80 hover:bg-white dark:hover:bg-gray-800 border-0 flex items-center justify-center shrink-0 aspect-square"
                                      title="Agregar a favoritos"
                                    >
                                      <Heart 
                                        className={cn(
                                          "h-5 w-5 transition-all",
                                          isFavorited(product.id) ? "fill-red-500 text-red-500" : "text-muted-foreground"
                                        )}
                                      />
                                    </button>
                                  </div>
                                </div>

                                {/* Product Info */}
                                <div className="flex-1 min-w-0 flex flex-col justify-between">
                                  <div>
                                    <button
                                      onClick={() => handleOpenProductSidebar(product)}
                                      className="block w-full min-w-0 text-sm font-medium text-foreground whitespace-normal break-words leading-snug group-hover:text-primary transition-colors hover:underline text-left cursor-pointer"
                                    >
                                      {product.title}
                                    </button>
                                    {product.description && (
                                      <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                                        {stripHtml(product.description)}
                                      </p>
                                    )}
                                  </div>
                                  <div className="flex items-center justify-between mt-2 gap-2">
                                    <div className="flex flex-col gap-0.5">
                                      <span className="text-sm font-bold text-foreground">
                                        {formatCurrency(product.fixed_sale_price || 0)}
                                      </span>
                                      {getCartItemByProductId(product.id) && (
                                        <span className="inline-flex items-center gap-1 w-fit px-1.5 py-0.5 rounded-full bg-primary/15 text-primary text-[10px] font-semibold leading-none">
                                          {addingProductIds.has(product.id) ? (
                                            <>
                                              <Loader2 className="w-2.5 h-2.5 shrink-0 animate-spin" />
                                              Agregando
                                            </>
                                          ) : (
                                            <>
                                              <ShoppingCart className="w-2.5 h-2.5 shrink-0" />
                                              Agregado
                                            </>
                                          )}
                                        </span>
                                      )}
                                    </div>
                                    <div className="h-[43px] flex items-center shrink-0" onClick={(e) => e.stopPropagation()}>
                                      {getCartItemByProductId(product.id) ? (
                                        <QuantitySelector
                                          cartItemId={getCartItemByProductId(product.id)?.id || ''}
                                          quantity={getCartItemByProductId(product.id)?.quantity || 1}
                                          onUpdateQuantity={(cartItemId, quantityCalculator) => {
                                            void handleUpdateQuantity(product.id, cartItemId, quantityCalculator);
                                          }}
                                          onPlayBeep={playBeep}
                                          onVibrate={vibrate}
                                          disabled={
                                            updatingProductIds.has(product.id) ||
                                            Boolean(getCartItemByProductId(product.id)?.id?.startsWith('pending-'))
                                          }
                                        />
                                      ) : (
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleAddToCart(product.id); }}
                                          className="h-[43px] w-[43px] p-0 rounded-full bg-primary text-white shadow-lg hover:shadow-xl transition-all active:scale-75 duration-75 flex items-center justify-center shrink-0 aspect-square"
                                          title="Agregar al canasto (1 unidad)"
                                        >
                                          <Plus className="h-6 w-6" />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                          </React.Fragment>
                        ))}
                      </>
                    )}
                  </>
                )}
              </div>
            </ScrollArea>

            <AnimatePresence>
              {filtersOpen && (
                <motion.div
                  initial={{ x: '100%' }}
                  animate={{ x: 0 }}
                  exit={{ x: '100%' }}
                  transition={{ duration: 0.2 }}
                  className="absolute inset-0 z-[20] flex flex-col bg-background"
                >
                  {/* Safe Area Spacer to match main header alignment */}
                  {!isDesktop && <div className="h-[env(safe-area-inset-top,0px)] bg-primary w-full shrink-0" />}
                  <div className="flex items-center justify-between p-4 border-b shrink-0 bg-primary text-primary-foreground min-h-[64px]">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Settings className="w-5 h-5" />
                      Filtros
                    </h3>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={resetFilters}
                        className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-white/15"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        Limpiar
                      </button>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              onClick={() => setFiltersOpen(false)}
                              className="p-1 hover:bg-white/10 text-white rounded-lg transition-colors hover:text-white"
                            >
                              <X className="w-5 h-5 text-white" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent variant="sidebar">
                            <p>Cerrar filtros</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>

                  <ScrollArea className="flex-1">
                    <div className="space-y-4 p-4">
                      <div className="rounded-2xl border border-border/60 bg-card p-3 shadow-sm space-y-2.5">
                        <label className="text-sm font-semibold text-foreground">Distancia máxima</label>
                        <Select value={distanceFilter} onValueChange={(value) => setDistanceFilter(value as DistanceFilter)}>
                          <SelectTrigger className="rounded-xl border-border/70 bg-background/80">
                            <SelectValue placeholder="Cualquiera" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="any">Cualquiera</SelectItem>
                            <SelectItem value="5">Hasta 5 km</SelectItem>
                            <SelectItem value="10">Hasta 10 km</SelectItem>
                            <SelectItem value="25">Hasta 25 km</SelectItem>
                            <SelectItem value="50">Hasta 50 km</SelectItem>
                            <SelectItem value="100">Hasta 100 km</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="rounded-2xl border border-border/60 bg-card p-3 shadow-sm space-y-2.5">
                        <label className="text-sm font-semibold text-foreground">Rango de precio</label>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Mínimo</label>
                            <Input
                              type="number"
                              inputMode="numeric"
                              placeholder="0"
                              value={minPriceFilter}
                              onChange={(e) => setMinPriceFilter(e.target.value)}
                              className="rounded-xl border-border/70 bg-background/80"
                            />
                          </div>
                          <div className="space-y-2">
                            <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Máximo</label>
                            <Input
                              type="number"
                              inputMode="numeric"
                              placeholder="50000"
                              value={maxPriceFilter}
                              onChange={(e) => setMaxPriceFilter(e.target.value)}
                              className="rounded-xl border-border/70 bg-background/80"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-border/60 bg-card p-3 shadow-sm space-y-2.5">
                        <label className="text-sm font-semibold text-foreground">Condición</label>
                        <Select value={conditionFilter} onValueChange={(value) => setConditionFilter(value as ConditionFilter)}>
                          <SelectTrigger className="rounded-xl border-border/70 bg-background/80">
                            <SelectValue placeholder="Cualquiera" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="any">Cualquiera</SelectItem>
                            <SelectItem value="new">Nuevo</SelectItem>
                            <SelectItem value="like_new">Como nuevo</SelectItem>
                            <SelectItem value="used_good">Usado buen estado</SelectItem>
                            <SelectItem value="used_fair">Usado estado regular</SelectItem>
                            <SelectItem value="for_parts">Para piezas</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="rounded-2xl border border-border/60 bg-card p-3 shadow-sm space-y-2.5">
                        <label className="text-sm font-semibold text-foreground">Remate</label>
                        <Select value={auctionFilter} onValueChange={(value) => setAuctionFilter(value as AuctionFilter)}>
                          <SelectTrigger className="rounded-xl border-border/70 bg-background/80">
                            <SelectValue placeholder="Todos" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="any">Todos</SelectItem>
                            <SelectItem value="auction">Sólo en remate</SelectItem>
                            <SelectItem value="fixed">Sólo venta directa</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="rounded-2xl border border-border/60 bg-card p-3 shadow-sm space-y-2.5">
                        <label className="text-sm font-semibold text-foreground">Publicación</label>
                        <Select value={dateFilter} onValueChange={(value) => setDateFilter(value as DateFilter)}>
                          <SelectTrigger className="rounded-xl border-border/70 bg-background/80">
                            <SelectValue placeholder="Cualquiera" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="any">Cualquiera</SelectItem>
                            <SelectItem value="24h">Últimas 24h</SelectItem>
                            <SelectItem value="7d">Últimos 7 días</SelectItem>
                            <SelectItem value="30d">Últimos 30 días</SelectItem>
                            <SelectItem value="365d">Último año</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="sticky bottom-0 pt-1 bg-gradient-to-t from-background via-background/95 to-transparent">
                        <button
                          type="button"
                          onClick={() => setFiltersOpen(false)}
                          className="w-full h-11 rounded-full bg-primary text-primary-foreground font-semibold shadow-md shadow-primary/25 hover:bg-primary/90 transition-colors"
                        >
                          Aplicar filtros
                        </button>
                      </div>
                    </div>
                  </ScrollArea>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </>
      )}
    </AnimatePresence>

    {/* Barcode Scanner Modal - Outside AnimatePresence to render above sidebar */}
    <BarcodeScannerModal 
      open={barcodeModalOpen} 
      onClose={() => setBarcodeModalOpen(false)}
      onDetected={(code) => {
        // When barcode is detected, set it as search term and trigger search
        setSearchInput(code);
        setSearchTerm(code);
        // Optionally close the modal
        setBarcodeModalOpen(false);
      }}
    />
    </>
  );
};
